import { useEffect, useMemo, useState } from 'react';

import { captureFallbackLanding } from '@/renderer/utils/stat/attribution';

import confMap from './confMap.json';
import template from './template.html?raw';

// CFB Results (#1495). An unlisted, publicly viewable promo archive of every graded
// RAS college football play. The page is a faithful port of a self-contained
// vanilla HTML/CSS/JS template ("the archive"), driven by our DB as the source of
// truth: the server endpoint below regenerates the columnar PAYLOAD from graded
// `releases` on each (cached) request, so the numbers stay current after grading.
//
// The template is rendered verbatim inside a full-viewport iframe (srcdoc). That
// isolation is deliberate — the template owns the whole document (its own dark
// theme, fonts, bare `body`/`table`/`select` CSS, and a script that drives the DOM
// by id). An iframe lets all of it run exactly as authored with zero collision
// against the React app's global styles and hydration. We inject only the data
// (rows) plus the template's own conference realignment map (kept client-side,
// unchanged from the original). Nothing in the app links here — like /vsin, the
// route is guarded only by being unlisted (removed from the sidebar via HIDE_ON).

const ENDPOINT = '/api/v1/cfb-results';
const PLACEHOLDER = '/*__PAYLOAD__*/';

// Match the template's own background so there is no white flash before the
// iframe mounts.
const SHELL_BG = '#0A0E13';

interface Payload {
  fields: string[];
  rows: unknown[][];
}

function buildSrcDoc(payload: Payload): string {
  // Serialize the data plus the conference map into the shape the template
  // expects (PAYLOAD = { fields, rows, conf }). Escape `<` so a value can never
  // terminate the inner <script>, and use a replacer function so `$` sequences in
  // play text are not treated as replacement patterns.
  const json = JSON.stringify({
    fields: payload.fields,
    rows: payload.rows,
    conf: confMap,
  }).replace(/</g, '\\u003c');
  return template.replace(PLACEHOLDER, () => json);
}

export default function Page() {
  const [payload, setPayload] = useState<Payload | null>(null);
  const [error, setError] = useState(false);

  // Funnel attribution for this promo. Inbound campaign links (utm/twclid) are
  // captured globally by captureAttribution() on landing; this only credits the
  // CFB Results page for direct / organic-search visitors who arrive with no
  // campaign signal and no prior touch — it never overwrites a real inbound
  // source, so appending utm params to the outbound CTA (which would clobber
  // inbound under the last-touch model) is deliberately avoided.
  useEffect(() => {
    captureFallbackLanding({
      utmSource: 'cfb-results',
      utmMedium: 'promo',
      landingPage: '/cfb-results',
    });
  }, []);

  useEffect(() => {
    let alive = true;
    fetch(ENDPOINT, { headers: { Accept: 'application/json' } })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data: unknown) => {
        if (!alive) {
          return;
        }
        const candidate = data as Partial<Payload> | null;
        if (!candidate || !Array.isArray(candidate.fields) || !Array.isArray(candidate.rows)) {
          throw new Error('Malformed payload');
        }
        setPayload(candidate as Payload);
      })
      .catch(() => {
        if (alive) {
          setError(true);
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  const srcDoc = useMemo(() => (payload ? buildSrcDoc(payload) : ''), [payload]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: SHELL_BG,
        color: '#8798AC',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {srcDoc ? (
        <iframe
          title="RAS College Football: CFB Results"
          srcDoc={srcDoc}
          style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            fontSize: 14,
            letterSpacing: '0.02em',
          }}
        >
          {error
            ? 'Could not load the results archive. Please refresh to try again.'
            : 'Loading the results archive…'}
        </div>
      )}
    </div>
  );
}
